import { type FC, type PropsWithChildren, useState } from 'react'
import classNames from 'classnames'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { type EmailTemplate, EmailTemplatePurpose, type SmsTemplate, SmsTemplatePurpose } from '../../../../models/Scheduling'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { useSchedulingEmailTemplates } from '../../../Scheduling/shared/useSchedulingEmailTemplates'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetSchedulingSmsTemplatesQuery } from '../../../../store/scheduling/scheduling'
import { SettingsCard, SettingsSection } from './SettingsSection'
import { ADVISOR_EMAIL_PURPOSES, CLIENT_EMAIL_PURPOSES, type EmailTemplatePurposeInfo } from './emailTemplatePurposes'
import { tokenizeTemplateText, transformTemplateBodyHtml } from './messageTemplatePlaceholders'

const CHIP_CLASS = 'zocks-px-0.25 zocks-bg-color-primary-light rounded-1'

interface TemplateTextProps {
  text: string
}

const TemplateText: FC<TemplateTextProps> = ({ text }) => (
  <>
    {tokenizeTemplateText(text).map((token, index) =>
      token.type === 'chip' ? (
        <span key={index} className={CHIP_CLASS}>
          {token.value}
        </span>
      ) : (
        <span key={index}>{token.value}</span>
      ),
    )}
  </>
)

interface TemplateCardProps {
  regionId: string
  label: string
  description: string
  isExpanded: boolean
  onToggle: () => void
}

const TemplateCard: FC<PropsWithChildren<TemplateCardProps>> = ({ regionId, label, description, isExpanded, onToggle, children }) => {
  const labelId = `${regionId}-label`

  return (
    <SettingsCard>
      <button
        type='button'
        className='d-flex align-items-center justify-content-between gap-3 py-3 zocks-px-1.25 border-0 bg-transparent text-start w-100'
        aria-expanded={isExpanded}
        aria-controls={regionId}
        onClick={onToggle}
      >
        <span className='d-flex flex-column'>
          <span id={labelId} className='bold'>
            {label}
          </span>
          <span className='paragraph-small zocks-text-gray-600'>{description}</span>
        </span>
        <i aria-hidden='true' className={classNames('bi zocks-text-gray-500', isExpanded ? 'bi-chevron-up' : 'bi-chevron-down')} />
      </button>
      {isExpanded ? (
        <div id={regionId} role='region' aria-labelledby={labelId} className='px-3 pb-3'>
          {children}
        </div>
      ) : null}
    </SettingsCard>
  )
}

interface EmailTemplatePreviewProps {
  template?: EmailTemplate
}

const EmailTemplatePreview: FC<EmailTemplatePreviewProps> = ({ template }) =>
  template != null ? (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex flex-column zocks-gap-0.5 p-3 rounded-2 zocks-bg-color-gray-100 paragraph-small'>
        <div className='d-flex align-items-baseline gap-2'>
          <span className='lead-small zocks-text-gray-500 text-uppercase'>From</span>
          <span>
            <span className={CHIP_CLASS}>Advisor full name</span> via Zocks · notifications@booking.zocks.com
          </span>
        </div>
        <div className='d-flex align-items-baseline gap-2'>
          <span className='lead-small zocks-text-gray-500 text-uppercase'>Subject</span>
          <span>
            <TemplateText text={template.subject} />
          </span>
        </div>
      </div>
      <div className='pe-none' dangerouslySetInnerHTML={{ __html: transformTemplateBodyHtml(template.body, CHIP_CLASS) }} />
    </div>
  ) : (
    <div className='paragraph-small zocks-text-gray-600'>This template isn&apos;t available yet.</div>
  )

interface SmsTemplatePreviewProps {
  template?: SmsTemplate
  note: string
}

const SmsTemplatePreview: FC<SmsTemplatePreviewProps> = ({ template, note }) =>
  template != null ? (
    <div className='d-flex flex-column gap-3 p-3 border rounded-2 paragraph-small'>
      <div className='d-flex align-items-baseline gap-2'>
        <span className='lead-small zocks-text-gray-500 text-uppercase'>From</span>
        <span>Your sending number</span>
      </div>
      <div className='p-3 rounded-3 zocks-bg-color-gray-100'>
        <TemplateText text={template.body} />
      </div>
      <span className='zocks-text-gray-600'>{note}</span>
    </div>
  ) : (
    <div className='paragraph-small zocks-text-gray-600'>This template isn&apos;t available yet.</div>
  )

interface SmsTemplateInfo {
  purpose: SmsTemplatePurpose
  label: string
  description: string
  note: string
}

const SMS_TEMPLATE_PURPOSES: SmsTemplateInfo[] = [
  {
    purpose: SmsTemplatePurpose.MEETING_REMINDER,
    label: 'Meeting reminder',
    description: 'Sent before meetings at the times set in Reminders.',
    note: 'Sent at the times set in Reminders. Not sent during quiet hours. Meeting reminder emails still go out.',
  },
  {
    purpose: SmsTemplatePurpose.CANCELLATION_NOTICE,
    label: 'Cancellation',
    description: 'Sent when you cancel a meeting.',
    note: 'Not sent during quiet hours. Cancellation emails still go out.',
  },
]

interface EmailTemplateGroupProps {
  title: string
  purposes: EmailTemplatePurposeInfo[]
  byPurpose: (purpose: EmailTemplatePurpose) => EmailTemplate | undefined
  expandedId: string | null
  onToggle: (id: string) => void
}

const TemplateGroup: FC<PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <div className='d-flex flex-column zocks-gap-0.75'>
    <h6 className='mb-0 zocks-text-gray-900'>{title}</h6>
    <div className='d-flex flex-column gap-2'>{children}</div>
  </div>
)

const EmailTemplateGroup: FC<EmailTemplateGroupProps> = ({ title, purposes, byPurpose, expandedId, onToggle }) => (
  <TemplateGroup title={title}>
    {purposes.map((info) => {
      const id = `email-template-${info.purpose}`
      return (
        <TemplateCard key={id} regionId={id} label={info.label} description={info.description} isExpanded={expandedId === id} onToggle={() => onToggle(id)}>
          <EmailTemplatePreview template={byPurpose(info.purpose)} />
        </TemplateCard>
      )
    })}
  </TemplateGroup>
)

interface SmsTemplateGroupProps {
  expandedId: string | null
  onToggle: (id: string) => void
}

const SmsTemplateGroup: FC<SmsTemplateGroupProps> = ({ expandedId, onToggle }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: templates = [], isLoading, isError } = useGetSchedulingSmsTemplatesQuery({ accountId })

  return (
    <TemplateGroup title='Texts sent to clients'>
      {isLoading ? (
        <div className='d-flex justify-content-center py-3'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600'>Could not load the text templates. Please try again.</div> : null}
      {!isLoading && !isError
        ? SMS_TEMPLATE_PURPOSES.map((info) => {
            const id = `sms-template-${info.purpose}`
            return (
              <TemplateCard
                key={id}
                regionId={id}
                label={info.label}
                description={info.description}
                isExpanded={expandedId === id}
                onToggle={() => onToggle(id)}
              >
                <SmsTemplatePreview template={templates.find((template) => template.purpose === info.purpose)} note={info.note} />
              </TemplateCard>
            )
          })
        : null}
    </TemplateGroup>
  )
}

export const MessageTemplatesSection: FC = () => {
  const { isLoading, isError, byPurpose } = useSchedulingEmailTemplates()
  const { hasQuota } = useZocksQuota()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const showSms = hasQuota('scheduling.smsNotifications.value')
  const emailTemplatesReady = !isLoading && !isError

  const clientPurposes = hasQuota('scheduling.reminders.value')
    ? CLIENT_EMAIL_PURPOSES
    : CLIENT_EMAIL_PURPOSES.filter((info) => info.purpose !== EmailTemplatePurpose.MEETING_REMINDER)

  const handleToggle = (id: string) => {
    setExpandedId((current) => (current === id ? null : id))
  }

  return (
    <SettingsSection
      title={showSms ? 'Message templates' : 'Email templates'}
      titleAdornment={
        <InfoTooltip
          title="These templates are managed for you at the moment. Let support know what you'd change and help us improve them."
          label={showSms ? 'About message templates' : 'About email templates'}
        />
      }
      subtitle={
        showSms
          ? 'The emails and texts Zocks sends for you, with details filled in automatically. Booking and reschedule confirmations come from your calendar, not from here.'
          : 'The scheduling emails Zocks sends for you, with details filled in automatically. Booking and reschedule confirmations come from your calendar, not from here.'
      }
    >
      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600'>Could not load the email templates. Please try again.</div> : null}
      {emailTemplatesReady ? (
        <EmailTemplateGroup
          title={showSms ? 'Emails sent to clients on your behalf' : 'Sent to clients on your behalf'}
          purposes={clientPurposes}
          byPurpose={byPurpose}
          expandedId={expandedId}
          onToggle={handleToggle}
        />
      ) : null}
      {showSms ? <SmsTemplateGroup expandedId={expandedId} onToggle={handleToggle} /> : null}
      {emailTemplatesReady ? (
        <EmailTemplateGroup
          title={showSms ? 'Emails sent to you' : 'Sent to you'}
          purposes={ADVISOR_EMAIL_PURPOSES}
          byPurpose={byPurpose}
          expandedId={expandedId}
          onToggle={handleToggle}
        />
      ) : null}
    </SettingsSection>
  )
}
